/**
 * The region inside a company's country whose own rules apply to it: the UK's
 * nations, Australia's states and territories. A company is in one
 * (tenants.default_nation), each branch in its own (branches.nation), and
 * library content lists the regions it applies to (nations[]); every company
 * read filters on the region of the branch being worked in.
 *
 * Stored as the ISO 3166-2 suffix, so country and region read 'GB-WLS' or
 * 'AU-NSW'. The Postgres enum `Nation` holds every code here; the guard
 * country-regions.test.ts fails when the two differ.
 */

export interface CountryRegion {
  code: Nation;
  name: string;
}

/** The UK's nations: England and Wales. Scotland and Northern Ireland are not on the platform. */
export type UkNation = 'ENG' | 'WLS';

/** Australia's six states and two territories. Each makes its own laws on safety, payroll tax, long service leave, holidays and medicines. */
export type AuState = 'ACT' | 'NSW' | 'NT' | 'QLD' | 'SA' | 'TAS' | 'VIC' | 'WA';

export type Nation = UkNation | AuState;

/** Each country with regions of its own, and what a region is called there. In alphabetical order of name. */
export const COUNTRY_REGIONS: Readonly<Record<string, { word: string; defaultCode?: Nation; regions: ReadonlyArray<CountryRegion> }>> = {
  GB: {
    word: 'Nation',
    defaultCode: 'ENG',
    regions: [
      { code: 'ENG', name: 'England' },
      { code: 'WLS', name: 'Wales' },
    ],
  },
  AU: {
    word: 'State or territory',
    regions: [
      { code: 'ACT', name: 'Australian Capital Territory' },
      { code: 'NSW', name: 'New South Wales' },
      { code: 'NT', name: 'Northern Territory' },
      { code: 'QLD', name: 'Queensland' },
      { code: 'SA', name: 'South Australia' },
      { code: 'TAS', name: 'Tasmania' },
      { code: 'VIC', name: 'Victoria' },
      { code: 'WA', name: 'Western Australia' },
    ],
  },
};

/** Every region code on the platform, for the API's input checks. */
export const ALL_REGION_CODES = Object.values(COUNTRY_REGIONS).flatMap((c) => c.regions.map((r) => r.code)) as [Nation, ...Nation[]];

/**
 * A country with no regions of its own still records each company as England,
 * the column's default, and its library content as England and Wales. That is
 * what every such company has carried since the column was made, and every
 * read path filters on it, so it stays until that country is given regions.
 */
const NO_REGIONS_COMPANY: Nation = 'ENG';
const NO_REGIONS_CONTENT: ReadonlyArray<Nation> = ['ENG', 'WLS'];

/** The country's own regions; empty for a country that has none. */
export function regionsOf(countryCode: string): ReadonlyArray<CountryRegion> {
  return COUNTRY_REGIONS[countryCode.toUpperCase()]?.regions ?? [];
}

/** What a region is called in this country: Nation in the UK, State or territory in Australia. */
export function regionWord(countryCode: string): string {
  return COUNTRY_REGIONS[countryCode.toUpperCase()]?.word ?? 'Nation';
}

export function regionName(code: string): string {
  for (const c of Object.values(COUNTRY_REGIONS)) {
    const hit = c.regions.find((r) => r.code === code);
    if (hit) return hit.name;
  }
  return code;
}

/** The regions a company, branch or library item of this country may carry. */
export function regionCodesFor(countryCode: string): ReadonlyArray<Nation> {
  const own = regionsOf(countryCode);
  return own.length > 0 ? own.map((r) => r.code) : NO_REGIONS_CONTENT;
}

export function regionBelongsTo(countryCode: string, code: string): boolean {
  return (regionCodesFor(countryCode) as ReadonlyArray<string>).includes(code);
}

/**
 * The region a new company of this country takes when none is chosen: the
 * country's defaultCode (England in the UK, as before regions could be
 * chosen), or null where one must be chosen because no region is the usual
 * case (Australia).
 */
export function defaultRegionFor(countryCode: string): Nation | null {
  const country = COUNTRY_REGIONS[countryCode.toUpperCase()];
  if (!country) return NO_REGIONS_COMPANY;
  return country.defaultCode ?? null;
}
